import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FC,
} from "react";
import { InfoCircleOutlined } from "@ant-design/icons";

import { AppStrings } from "../../../../utils/constants/AppStrings";
import { Button, Modal } from "../../../shared";
import SeasonOverview from "../../CreateSeason/SeasonOverview/SeasonOverview";
import Schedule from "../../CreateSeason/Schedule/Schedule";
import {
  isScheduleValid,
  isSeasonOverviewValid,
  type AutoStartAnchor,
  type ScheduleValues,
  type SeasonOverviewValues,
} from "../../CreateSeason/createSeasonData";

import styles from "./EditSeasonModal.module.css";

/**
 * Canonical value contract for `EditSeasonModal`. Composes the same
 * `SeasonOverviewValues` and `ScheduleValues` shapes the Create Season
 * wizard uses for its Step 1 + Step 3 — keeping the modal's source of
 * truth aligned with the wizard's so a single API mapper can serve
 * both surfaces.
 */
export type EditSeasonValues = SeasonOverviewValues & {
  schedule: ScheduleValues;
};

export type EditSeasonModalProps = {
  open: boolean;
  /** Pre-formatted heading suffix — typically the current season name
   *  ("June 2025 Season"). Rendered inside the modal title between
   *  curly quotes per the Figma. */
  seasonName: string;
  initialValues: EditSeasonValues;
  /** Fired with the latest snapshot when the user clicks the primary
   *  Save button — only invoked while the disabled-Save gate is met. */
  onSave: (values: EditSeasonValues) => void;
  /** Fired when the user clicks "Save as Draft". Persistence is mock
   *  today, so the parent typically navigates away. */
  onSaveAsDraft: (values: EditSeasonValues) => void;
  /** Fired after the user confirms the nested destructive
   *  confirmation modal — the parent is responsible for closing both
   *  modals (this one will also self-close). */
  onDelete: () => void;
  /** Fired when the user dismisses via the top-right close button,
   *  backdrop click, or escape. */
  onClose: () => void;
  destructiveActionLabel?: string;
  destructiveActionTitle?: string;
  destructiveActionBodyPrefix?: string;
  destructiveActionBodySuffix?: string;
  destructiveActionConfirmCta?: string;
  showDestructiveAction?: boolean;
  /** Whether the destructive-confirmation body interpolates the season name
   *  between the prefix and suffix. `true` for copy that reads as
   *  `"… unschedule "Name"?"`; pass `false` for a self-contained sentence
   *  (e.g. the delete copy) that carries no inline name. */
  showSeasonName?: boolean;
  /** When `true`, the primary Save button shows the standard button
   *  loader while the parent's update request is in flight. */
  isSaving?: boolean;
  /** The active season this one auto-starts after — forwarded to the embedded
   *  `Schedule` so toggling auto-start fills the locked start (active end + 1
   *  min), a default end (+3 days), and the `trigger_season_id` link. */
  autoStartAnchor?: AutoStartAnchor | null;
  /** Renders the modal as a read-only snapshot reached from the eye icon on
   *  a locked / active season (which has no edit affordance). Every field is
   *  disabled, the footer (Save / Save as Draft / destructive action) is
   *  removed entirely, and the title reads `View "<name>"` instead of
   *  `Edit "<name>"`. Mirrors the `mode="view"` read-only flow on
   *  `CreateTaskModal`. */
  readOnly?: boolean;
};

const splitOverview = (
  values: EditSeasonValues,
): SeasonOverviewValues => ({
  name: values.name,
  description: values.description,
  tiers: values.tiers,
  sportsbookSubmissionMode: values.sportsbookSubmissionMode,
  casinoSubmissionMode: values.casinoSubmissionMode,
  imageFile: values.imageFile,
  imgKey: values.imgKey,
  imageUrl: values.imageUrl,
});

/**
 * Whether the user has supplied enough data for the primary Save
 * button to enable. Delegates to the wizard's per-step validators
 * (`isSeasonOverviewValid` for name+description+tiers,
 * `isScheduleValid` for the auto-start / date+time rule) so the modal
 * and the wizard's Next gate stay in lock-step — adding a rule in one
 * place automatically applies in the other.
 *
 * Exported so the test suite can exercise the rule independently of
 * the rendered UI.
 */
export const isEditSeasonValid = (values: EditSeasonValues): boolean =>
  isSeasonOverviewValid(splitOverview(values)) &&
  isScheduleValid(values.schedule);

/**
 * Pencil-icon edit modal for the editable Season Details General card.
 * Composes the same `SeasonOverview` (Step 1) and `Schedule` (Step 3)
 * components the Create Season wizard uses, both in their
 * `variant="embedded"` mode so the modal's own section headers + info
 * icons wrap them. The disabled-Save gate is `isEditSeasonValid`,
 * which itself delegates to the wizard's per-step validators.
 *
 * Controlled-output: the parent owns persistence. `onSave` /
 * `onSaveAsDraft` deliver the full snapshot; the modal manages its
 * own draft state internally so an in-progress edit can be cancelled
 * cleanly by closing.
 *
 * Delete uses a nested destructive confirmation modal (same pattern as
 * `ConfirmDeleteChallengeModal` / the End Season confirm modal in
 * `SeasonDetails`) so an accidental click can't blow away a season.
 */
const EditSeasonModal: FC<EditSeasonModalProps> = ({
  open,
  seasonName,
  initialValues,
  onSave,
  onSaveAsDraft,
  onDelete,
  onClose,
  destructiveActionLabel = AppStrings.EDIT_SEASON_DELETE_SEASON,
  destructiveActionTitle = AppStrings.DELETE_SEASON_MODAL_TITLE,
  destructiveActionBodyPrefix = AppStrings.DELETE_SEASON_CONFIRM_BODY_PREFIX,
  destructiveActionBodySuffix = AppStrings.DELETE_SEASON_CONFIRM_BODY_SUFFIX,
  destructiveActionConfirmCta = AppStrings.DELETE_SEASON_CONFIRM_CTA,
  showDestructiveAction = true,
  showSeasonName = true,
  isSaving = false,
  readOnly = false,
  autoStartAnchor = null,
}) => {
  const [draft, setDraft] = useState<EditSeasonValues>(initialValues);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  // Re-seed the draft each time the modal is opened so a stale draft
  // from a previous open can't leak into the next session. We also
  // re-seed when `initialValues` changes while open so the parent can
  // surface a server-side update without remounting.
  useEffect(() => {
    if (open) {
      setDraft(initialValues);
      setIsDeleteConfirmOpen(false);
    }
  }, [open, initialValues]);

  const handleOverviewChange = useCallback(
    (next: SeasonOverviewValues) => {
      setDraft((current) => ({ ...current, ...next }));
    },
    [],
  );

  const handleScheduleChange = useCallback(
    (next: ScheduleValues) => {
      setDraft((current) => ({ ...current, schedule: next }));
    },
    [],
  );

  const handleSave = useCallback(() => {
    if (!isEditSeasonValid(draft)) return;
    onSave(draft);
  }, [draft, onSave]);

  const handleSaveAsDraft = useCallback(() => {
    onSaveAsDraft(draft);
  }, [draft, onSaveAsDraft]);

  const handleOpenDeleteConfirm = useCallback(
    () => setIsDeleteConfirmOpen(true),
    [],
  );
  const handleCancelDeleteConfirm = useCallback(
    () => setIsDeleteConfirmOpen(false),
    [],
  );
  const handleConfirmDelete = useCallback(() => {
    setIsDeleteConfirmOpen(false);
    onDelete();
  }, [onDelete]);

  const overviewSlice = useMemo(() => splitOverview(draft), [draft]);
  const canSave = useMemo(() => isEditSeasonValid(draft), [draft]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="xl"
      destroyOnHidden
      title={
        <span
          className={styles.titleRow}
          data-testid="edit-season-modal-title"
        >
          <span>{`${
            readOnly
              ? AppStrings.VIEW_SEASON_MODAL_TITLE_PREFIX
              : AppStrings.EDIT_SEASON_MODAL_TITLE_PREFIX
          } `}</span>
          <span className={styles.titleSeasonName}>{`"${seasonName}"`}</span>
        </span>
      }
      footer={
        readOnly ? null : (
        <div className={styles.footer}>
          {showDestructiveAction ? (
            <Button
              intent="danger"
              variant="outline"
              className={styles.deleteButton}
              onClick={handleOpenDeleteConfirm}
              data-testid="edit-season-modal-delete"
            >
              {destructiveActionLabel}
            </Button>
          ) : (
            <span />
          )}
          <div className={styles.footerActions}>
            <Button
              intent="neutral"
              variant="outline"
              onClick={handleSaveAsDraft}
              data-testid="edit-season-modal-save-draft"
            >
              {AppStrings.SAVE_AS_DRAFT}
            </Button>
            <Button
              intent="primary"
              onClick={handleSave}
              disabled={!canSave || isSaving}
              loading={isSaving}
              data-testid="edit-season-modal-save"
            >
              {AppStrings.EDIT_SEASON_SAVE}
            </Button>
          </div>
        </div>
        )
      }
    >
      <div className={styles.body} data-testid="edit-season-modal">
        {/* ── General ──────────────────────────────────────────────── */}
        <section
          className={styles.section}
          aria-labelledby="edit-season-modal-general-heading"
        >
          <h3
            id="edit-season-modal-general-heading"
            className={styles.sectionHeader}
          >
            <span>{AppStrings.EDIT_SEASON_GENERAL_HEADING}</span>
            <InfoCircleOutlined
              className={styles.sectionInfoIcon}
              role="img"
              aria-label={AppStrings.EDIT_SEASON_GENERAL_INFO_ARIA}
              data-testid="edit-season-modal-general-info"
            />
          </h3>

          <SeasonOverview
            variant="embedded"
            values={overviewSlice}
            onValuesChange={handleOverviewChange}
            disabled={readOnly}
            testIdPrefix="edit-season-modal"
          />
        </section>

        <hr className={styles.divider} aria-hidden="true" />

        {/* ── Scheduling ───────────────────────────────────────────── */}
        <section
          className={styles.section}
          aria-labelledby="edit-season-modal-scheduling-heading"
        >
          <h3
            id="edit-season-modal-scheduling-heading"
            className={styles.sectionHeader}
          >
            <span>{AppStrings.EDIT_SEASON_SCHEDULING_HEADING}</span>
            <InfoCircleOutlined
              className={styles.sectionInfoIcon}
              role="img"
              aria-label={AppStrings.EDIT_SEASON_SCHEDULING_INFO_ARIA}
              data-testid="edit-season-modal-scheduling-info"
            />
          </h3>

          <Schedule
            variant="embedded"
            values={draft.schedule}
            onValuesChange={handleScheduleChange}
            disabled={readOnly}
            testIdPrefix="edit-season-modal"
            autoStartAnchor={autoStartAnchor}
          />
        </section>
      </div>

      {/* ── Nested destructive confirmation modal ─────────────────── */}
      <Modal
        open={isDeleteConfirmOpen}
        onClose={handleCancelDeleteConfirm}
        intent="destructive"
        size="sm"
        destroyOnHidden
        title={
          <span className={styles.deleteConfirmTitle}>
            {destructiveActionTitle}
          </span>
        }
        footer={
          <div className={styles.deleteConfirmFooter}>
            <Button
              intent="neutral"
              variant="outline"
              onClick={handleCancelDeleteConfirm}
              data-testid="edit-season-modal-delete-go-back"
            >
              {AppStrings.DELETE_SEASON_GO_BACK}
            </Button>
            <Button
              intent="danger"
              variant="outline"
              className={styles.deleteConfirmButton}
              onClick={handleConfirmDelete}
              data-testid="edit-season-modal-delete-confirm"
            >
              {destructiveActionConfirmCta}
            </Button>
          </div>
        }
      >
        <div data-testid="edit-season-modal-delete-confirm-modal">
          <p className={styles.deleteConfirmBody}>
            {`${destructiveActionBodyPrefix} `}
            {showSeasonName && (
              <span className={styles.deleteConfirmName}>
                {`"${seasonName}"`}
              </span>
            )}
            {destructiveActionBodySuffix}
          </p>
        </div>
      </Modal>
    </Modal>
  );
};

export default EditSeasonModal;
